import type {
  ExecutionEngineLiveJsonValue,
  ExecutionLiveSnapshot,
} from "@repo/execution-engine-client";
import { createCorrelationId } from "@repo/domain-events";

type ExecutionLiveOperation = ExecutionLiveSnapshot["operation"];
type ExecutionLiveJsonObject = {
  readonly [key: string]: ExecutionEngineLiveJsonValue;
};

const EVM_NATIVE_TOKEN_PLACEHOLDER =
  "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee";
const EXECUTION_REQUEST_KEY_STORAGE_PREFIX =
  "glider:webapp-v2:execution-request-key";
const EXECUTION_REQUEST_KEY_TTL_MS = 24 * 60 * 60 * 1000;

type StoredExecutionRequestKey = {
  readonly correlationId: string | null;
  readonly createdAt: number;
  readonly requestKey: string;
};

export type ExecutionRequestContext = {
  readonly correlationId: string;
  readonly requestKey: string;
};

function readEvmChainIdFromAssetId(assetId: string): number | null {
  const match = /^eip155:(\d+)\//.exec(assetId);
  if (!match?.[1]) {
    return null;
  }

  const chainId = Number.parseInt(match[1], 10);
  return Number.isInteger(chainId) && chainId > 0 ? chainId : null;
}

export function readEvmChainIdFromChainRef(chainRef: string): number | null {
  const match = /^(?:eip155:)?(\d+)$/.exec(chainRef.trim());
  if (!match?.[1]) {
    return null;
  }

  const chainId = Number.parseInt(match[1], 10);
  return Number.isInteger(chainId) && chainId > 0 ? chainId : null;
}

export function toExecutionEvmAsset(input: {
  assetId: string;
  chainRef: string;
}): { assetId: string; chainId: number } | null {
  const caipChainId = readEvmChainIdFromAssetId(input.assetId);
  if (caipChainId) {
    return {
      assetId: input.assetId.toLowerCase(),
      chainId: caipChainId,
    };
  }

  const chainId = readEvmChainIdFromChainRef(input.chainRef);
  if (!chainId) {
    return null;
  }

  // Positions can still arrive from deprecated canonical IDs. Convert at the
  // UI boundary so every execution-engine mutation speaks CAIP asset IDs.
  const legacyMatch = /^(0x[a-fA-F0-9]{40}):\d+$/.exec(input.assetId.trim());
  if (!legacyMatch?.[1]) {
    return null;
  }

  const tokenAddress = legacyMatch[1].toLowerCase();
  return {
    assetId:
      tokenAddress === EVM_NATIVE_TOKEN_PLACEHOLDER ||
      tokenAddress === "0x0000000000000000000000000000000000000000"
        ? `eip155:${chainId}/slip44:60`
        : `eip155:${chainId}/erc20:${tokenAddress}`,
    chainId,
  };
}

export function createPortfolioRequestKey(params: {
  action: "bridge" | "deposit" | "transfer" | "withdraw";
  portfolioId: string;
}): string {
  const random =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}:${Math.random().toString(16).slice(2)}`;
  return `${params.action}:${params.portfolioId}:${random}`;
}

function readStorage(): Storage | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function readStoredExecutionRequestKey(
  value: string | null,
): StoredExecutionRequestKey | null {
  if (!value) {
    return null;
  }

  try {
    const parsed = JSON.parse(value) as unknown;
    if (!parsed || typeof parsed !== "object") {
      return null;
    }

    const record = parsed as Record<string, unknown>;
    const requestKey = record.requestKey;
    const createdAt = record.createdAt;
    const correlationId = record.correlationId;
    if (
      typeof requestKey !== "string" ||
      requestKey.trim().length === 0 ||
      typeof createdAt !== "number" ||
      !Number.isFinite(createdAt)
    ) {
      return null;
    }

    return {
      correlationId:
        typeof correlationId === "string" && correlationId.trim().length > 0
          ? correlationId
          : null,
      createdAt,
      requestKey,
    };
  } catch {
    return null;
  }
}

function storageKeyForExecutionRequest(input: {
  namespace: string;
  fingerprintParts: readonly string[];
}): string {
  return [
    EXECUTION_REQUEST_KEY_STORAGE_PREFIX,
    input.namespace,
    ...input.fingerprintParts,
  ]
    .map((part) => encodeURIComponent(part))
    .join(":");
}

export function readOrCreateStoredExecutionRequestContext(input: {
  createRequestKey: () => string;
  fingerprintParts: readonly string[];
  namespace: string;
}): ExecutionRequestContext {
  const key = storageKeyForExecutionRequest(input);
  const storage = readStorage();
  const now = Date.now();
  const stored = readStoredExecutionRequestKey(storage?.getItem(key) ?? null);

  if (
    stored &&
    now - stored.createdAt >= 0 &&
    now - stored.createdAt <= EXECUTION_REQUEST_KEY_TTL_MS
  ) {
    const correlationId = stored.correlationId ?? createCorrelationId();
    if (!stored.correlationId) {
      storage?.setItem(
        key,
        JSON.stringify({
          correlationId,
          createdAt: stored.createdAt,
          requestKey: stored.requestKey,
        }),
      );
    }
    return {
      correlationId,
      requestKey: stored.requestKey,
    };
  }

  const requestKey = input.createRequestKey();
  const correlationId = createCorrelationId();
  storage?.setItem(
    key,
    JSON.stringify({
      correlationId,
      createdAt: now,
      requestKey,
    }),
  );
  return { correlationId, requestKey };
}

export function readOrCreateStoredExecutionRequestKey(input: {
  createRequestKey: () => string;
  fingerprintParts: readonly string[];
  namespace: string;
}): string {
  return readOrCreateStoredExecutionRequestContext(input).requestKey;
}

export function clearStoredExecutionRequestKey(input: {
  fingerprintParts: readonly string[];
  namespace: string;
}): void {
  readStorage()?.removeItem(storageKeyForExecutionRequest(input));
}

export function readOrCreatePortfolioRequestKey(input: {
  action: "bridge" | "deposit" | "transfer" | "withdraw";
  fingerprintParts: readonly string[];
  portfolioId: string;
}): string {
  return readOrCreatePortfolioRequestContext(input).requestKey;
}

export function readOrCreatePortfolioRequestContext(input: {
  action: "bridge" | "deposit" | "transfer" | "withdraw";
  fingerprintParts: readonly string[];
  portfolioId: string;
}): ExecutionRequestContext {
  return readOrCreateStoredExecutionRequestContext({
    namespace: `portfolio:${input.action}:${input.portfolioId}`,
    fingerprintParts: input.fingerprintParts,
    createRequestKey: () =>
      createPortfolioRequestKey({
        action: input.action,
        portfolioId: input.portfolioId,
      }),
  });
}

export function clearPortfolioRequestKey(input: {
  action: "bridge" | "deposit" | "transfer" | "withdraw";
  fingerprintParts: readonly string[];
  portfolioId: string;
}): void {
  clearStoredExecutionRequestKey({
    namespace: `portfolio:${input.action}:${input.portfolioId}`,
    fingerprintParts: input.fingerprintParts,
  });
}

export function isCashflowOperation(
  operation: ExecutionLiveOperation,
): boolean {
  return (
    operation.kind === "bridge" ||
    operation.kind === "deposit" ||
    (operation.kind === "execution" &&
      typeof operation.refs.marketOrder === "object" &&
      operation.refs.marketOrder !== null &&
      !Array.isArray(operation.refs.marketOrder)) ||
    operation.kind === "transfer" ||
    operation.kind === "withdraw"
  );
}

function isJsonArray(
  value: ExecutionEngineLiveJsonValue | undefined,
): value is readonly ExecutionEngineLiveJsonValue[] {
  return Array.isArray(value);
}

function readJsonObject(
  value: ExecutionEngineLiveJsonValue | undefined,
): ExecutionLiveJsonObject | null {
  return typeof value === "object" && value !== null && !isJsonArray(value)
    ? value
    : null;
}

function readJsonArray(
  value: ExecutionEngineLiveJsonValue | undefined,
): readonly ExecutionEngineLiveJsonValue[] {
  return isJsonArray(value) ? value : [];
}

function readJsonString(
  value: ExecutionEngineLiveJsonValue | undefined,
): string | null {
  const trimmed = typeof value === "string" ? value.trim() : "";
  return trimmed.length > 0 ? trimmed : null;
}

export function readCashflowPortfolioIds(
  operation: ExecutionLiveOperation,
): string[] {
  const portfolioIds = new Set<string>();
  const addPortfolioId = (value: string | null): void => {
    if (value) {
      portfolioIds.add(value);
    }
  };

  addPortfolioId(operation.portfolioId.trim() || null);

  for (const scopeValue of readJsonArray(operation.refs.portfolioScopes)) {
    const scope = readJsonObject(scopeValue);
    if (scope) {
      addPortfolioId(readJsonString(scope.portfolioId));
    }
  }

  const transfer = readJsonObject(operation.refs.transfer);
  if (transfer) {
    addPortfolioId(readJsonString(transfer.sourcePortfolioId));
    addPortfolioId(readJsonString(transfer.destinationPortfolioId));
  }

  return [...portfolioIds];
}

export function readFloatRecoverySettlementRefreshKey(
  operation: ExecutionLiveOperation | null | undefined,
): string | null {
  if (!operation) {
    return null;
  }

  const hint = readJsonObject(operation.refs.floatRecoverySettlement);
  const advanceId = hint ? readJsonString(hint.advanceId) : null;
  const chainRefs = hint ? readJsonArray(hint.chainRefs) : [];
  if (
    !advanceId ||
    chainRefs.length === 0 ||
    chainRefs.some((chainRef) => readJsonString(chainRef) === null)
  ) {
    return null;
  }

  return JSON.stringify([operation.operationId, advanceId]);
}

export function isIndexerSettledCashflowOperation(
  operation: ExecutionLiveOperation | null | undefined,
): boolean {
  if (!operation) {
    return false;
  }

  return (
    isCashflowOperation(operation) &&
    operation.state === "completed" &&
    operation.currentPhase?.toLowerCase() === "settled"
  );
}

// Phases at which the money movement is already acknowledged onchain while
// the operation is still in flight: engine-submitted legs report `confirmed`
// once the receipt lands, deposits report `indexing` when the user's
// transaction hash is known and `waiting_settlement` while the ledger poll
// runs. `settled` covers the transient frame before the state flips to
// `completed` (and the delayed park, which is settlement lag, not failure).
const ONCHAIN_ACKED_CASHFLOW_PHASES: ReadonlySet<string> = new Set([
  "confirmed",
  "indexing",
  "waiting_settlement",
  "settled",
]);

export function isOnchainAckedCashflowOperation(
  operation: ExecutionLiveOperation | null | undefined,
): boolean {
  if (!operation) {
    return false;
  }

  return (
    isCashflowOperation(operation) &&
    (operation.state === "running" || operation.state === "awaiting_user") &&
    ONCHAIN_ACKED_CASHFLOW_PHASES.has(
      operation.currentPhase?.toLowerCase() ?? "",
    )
  );
}
